import { Route } from 'react-router-dom'
import ProtectedRoute from '../components/ProtectedRoute'
import {
    DialogueTrainerPage,
    EmployeeLearningProfile,
    GrowthAnalyticsPage,
    GrowthKbPage,
    GrowthMePage,
    GrowthTeamCoursesPage,
    GrowthTeamPage,
    GrowthTeamReflectionsPage,
    LearningContentQualityPage,
    MorningDebriefPage,
    MyCalibrationPage,
    MyDocumentsPage,
    MyPayslipPage,
    MySkillProfilePage,
    PersonaLibraryPage,
    RepetitionRedirect,
    ReviewQueuePage,
    SrReviewPage,
    TeamReadinessPage,
    TrainerVsRealPage,
    TrainingPage,
    TrainingShell,
    VoiceRoleplayPage,
} from './lazyPages'

/*
 * Маршруты раздела «Обучение и развитие» и связанного личного кабинета.
 *
 * Вынесены из App.jsx: файл упёрся в гейт размера (48 КБ), и его собственный
 * комментарий требует разносить маршруты по routes/*.jsx, а не поднимать
 * планку. Это функция, а не компонент: <Routes> принимает только <Route> и
 * фрагменты, поэтому группу подставляем вызовом — {learningRoutes()}.
 */
export function learningRoutes() {
    return (
        <>
            {/* Раздел «Обучение и развитие» — общий каркас с
                липкой полосой вкладок (Каталог / Тренажёр /
                Треки / Калькулятор / Команда / Аналитика /
                Проверка ответов). Подстраницы рендерятся внутри
                Outlet — пользователь не теряет меню при переходе
                между ними. */}
            <Route path="training" element={<ProtectedRoute moduleKey="training"><TrainingShell /></ProtectedRoute>}>
                <Route index element={<TrainingPage />} />
                <Route path="review" element={<ReviewQueuePage />} />
                {/* Phase 4.1: card-level повторения переехали в Daily Plan
                    (см. SpacedRepRunnerModal в /me/learning). Старый путь
                    рендерит компонент-редирект с toast'ом. */}
                <Route path="repetition" element={<RepetitionRedirect />} />
                <Route path="personas" element={<PersonaLibraryPage />} />
                {/* Item Analysis (PR-4) — для методолога/HR/admin. Permission проверяется на компоненте. */}
                <Route path="quality" element={<LearningContentQualityPage />} />
            </Route>
            {/* Manager Learning Analytics — drill-down профиль обучения сотрудника. */}
            <Route path="training/employee/:employeeId" element={<ProtectedRoute moduleKey="training"><EmployeeLearningProfile /></ProtectedRoute>} />
            {/* «Моё обучение» — self-view персонального Дневника обучения.
                Доступен всем авторизованным с привязанным Employee (HARD
                GATE 1 self-view bypass в LearningAnalyticsGate). Без
                ProtectedRoute — личный кабинет не должен зависеть от
                видимости модуля `training` (сотрудник может не иметь
                wider Training-модуля, но право на свой профиль есть). */}
            <Route path="me/learning" element={<EmployeeLearningProfile isSelfView />} />
            {/* «Повторение материала» (Spaced Repetition) — LMS Senate Exchange MVP Stream 1.
                Self-view, employee-id resolved via JWT в backend. Без ProtectedRoute. */}
            <Route path="me/learning/sr" element={<SrReviewPage />} />
            {/* «Голос — английский» — Voice Roleplay (Phase 3.1 L&D Super-UX).
                Personal-cabinet sub-route /me/learning/voice. employee_id из
                JWT в backend /api/v1/voice/judge. ProtectedRoute
                moduleKey="me/learning/voice" — admin может скрыть для тенанта
                целиком (RAPID lock: voice_mandatory=OFF, opt-in by default).
                TODO Phase 2 integration: добавить «Запустить voice» action
                в DailyPlanItemCard когда kind==next_step (зависит от мержа
                feat(learning) /me/learning Daily Plan v2 hero — PR-2.2). */}
            <Route path="me/learning/voice" element={<ProtectedRoute moduleKey="me/learning/voice"><VoiceRoleplayPage /></ProtectedRoute>} />
            {/* «Профиль навыков» — Skill Profile v1 (Phase 4.2 L&D Super-UX).
                Self-view долгосрочной картины: focus skills + signals timeline +
                evidence badges + share-with-manager toggle. Без ProtectedRoute
                moduleKey (личный кабинет), но backend проверяет employee_id
                (404 при отсутствии). См. docs/frame/learning-development-ux-2026q2/
                multi-pr-plan.md PR 4.2. */}
            <Route path="me/skill-profile" element={<MySkillProfilePage />} />
            {/* «Моя зарплата» — self-service расчётный лист, доступен любому сотруднику (scope по себе). */}
            <Route path="me/payslip" element={<MyPayslipPage />} />
            {/* «Мои документы» — личный кабинет signing (B2-B8 PR-3).
                Список SigningRequest по сотруднику (Equipment Receipt /
                Outboarding / Probation Ack). Backend owner-check
                user.employee_id == :employee_id. Без ProtectedRoute moduleKey. */}
            <Route path="me/documents" element={<MyDocumentsPage />} />
            {/* «Вчерашний разбор» — DialogueTrainer v2 Wave 2 PR-5. Owner-only
                (employee.user_id == current_user.id); backend возвращает 404
                для чужих debrief'ов. Без ProtectedRoute moduleKey — личный
                кабинет, виден всем кто связан с Employee. */}
            <Route path="me/morning-debrief" element={<ProtectedRoute moduleKey="me/morning-debrief"><MorningDebriefPage /></ProtectedRoute>} />
            {/* «Моя калибровка» — DialogueTrainer v2 Wave 3 PR-6. Owner-only:
                мой тренажёр vs мои реальные сделки. Backend фильтрует по
                current_user.employee_id; чужие данные утечь не могут (нет
                employee_id-параметра). Без ProtectedRoute moduleKey — личный
                кабинет, виден всем кто связан с Employee. */}
            <Route path="me/calibration" element={<MyCalibrationPage />} />
            {/* «Калибровка тренажёра» — DialogueTrainer v2 Wave 2 PR-5 scatter.
                Backend GATE: team.insights.read. moduleKey="trainer-vs-real"
                в module_seed.py разрешает admin/hr/owner/ceo/dept_head/head/
                senior_online_manager. */}
            <Route
                path="team-insights/trainer-vs-real"
                element={<ProtectedRoute moduleKey="trainer-vs-real"><TrainerVsRealPage /></ProtectedRoute>}
            />
            {/* «Готовность команды» — DialogueTrainer v2 Wave 3 PR-5.
                N × 7 heatmap по customer-journey шагам. Backend GATE:
                team.insights.read. moduleKey="team-readiness" в
                module_seed.py разрешает admin/hr/owner/ceo/dept_head/
                head/senior_online_manager. */}
            <Route
                path="team-insights/readiness"
                element={<ProtectedRoute moduleKey="team-readiness"><TeamReadinessPage /></ProtectedRoute>}
            />
            {/* «Моё развитие» — Employee Growth Platform. Phase + playbook + ритуалы рефлексии.
                Без ProtectedRoute moduleKey (личный кабинет), но требует employee_id (gate в backend). */}
            <Route path="growth-me" element={<GrowthMePage />} />
            {/* «Развитие команды» — manager-view. ProtectedRoute moduleKey=growth-team (RBAC через module_seed allowed_roles). */}
            <Route path="growth-team" element={<ProtectedRoute moduleKey="growth-team"><GrowthTeamPage /></ProtectedRoute>} />
            {/* «Прогресс по курсам команды» — drill-down из growth-team. Тот же RBAC scope
                что и /growth/team/phases — phase.view.team или phase.view.all. */}
            <Route path="growth-team/courses" element={<ProtectedRoute moduleKey="growth-team"><GrowthTeamCoursesPage /></ProtectedRoute>} />
            {/* Manager-view рефлексии сотрудника по капстоун-уроку. RBAC enforced на backend (admin or manager).
                Открывается по action_url из notification «Сотрудник отправил рефлексию по уроку» (PR-B.3). */}
            <Route path="growth/team/:employeeUserId/reflections/:courseSlug/:lessonSlug" element={<GrowthTeamReflectionsPage />} />
            {/* «База знаний» — KB Artifact editor + review flow (PR 2.5). ProtectedRoute moduleKey=growth-kb. */}
            <Route path="growth-kb" element={<ProtectedRoute moduleKey="growth-kb"><GrowthKbPage /></ProtectedRoute>} />
            {/* «Аналитика развития» — analytics dashboard (PR 3.4). ProtectedRoute moduleKey=growth-analytics. */}
            <Route path="growth-analytics" element={<ProtectedRoute moduleKey="growth-analytics"><GrowthAnalyticsPage /></ProtectedRoute>} />
            <Route path="dialogue-trainer" element={<ProtectedRoute moduleKey="dialogue-trainer"><DialogueTrainerPage /></ProtectedRoute>} />
        </>
    )
}
